import React, { useState } from "react"
import PropTypes from "prop-types"
import { Empty, Input, Select } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import styles from "./index.module.css"

/**
 * The "Replace with" picker on one row of the Replace sheet.
 *
 * The search sits inside the panel rather than in the closed field, which is what the design
 * draws and what `BlackoutTargetSelector` already does on the settings page — so the two pickers
 * in the same feature behave the same way. That means `dropdownRender` owns the list, and
 * `options` is left in place only so the closed field can resolve the picked value's label.
 *
 * Each row keeps its own search text: the rows are independent choices, and a guide who typed
 * "Mark" on the first booking should not find the second one pre-filtered.
 */
const ReplacePicker = ({ value, options, placeholder, onChange, isDisabled = false, isInvalid = false }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState("")

  const normalizedSearch = searchText.trim().toLowerCase()
  const visibleOptions = normalizedSearch
    ? options.filter((option) => option.searchText.includes(normalizedSearch))
    : options

  const handlePick = (option) => {
    onChange(option.value)
    setIsOpen(false)
    setSearchText("")
  }

  const renderDropdown = () => (
    <section className={styles.dropdown} onClick={(event) => event.stopPropagation()}>
      <div className={styles.searchWrapper}>
        <Input
          allowClear
          placeholder={t("global.search", { defaultValue: "Search" })}
          prefix={<Icon name="Search" width={16} height={16} color="var(--color-text-secondary-dark)" />}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>

      <div className={styles.optionList}>
        {visibleOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={styles.optionRow}
            aria-pressed={option.value === value}
            onClick={() => handlePick(option)}
          >
            <span className={styles.optionName}>{option.name}</span>
            {option.category && <span className={styles.optionCategory}>({option.category})</span>}
          </button>
        ))}

        {!visibleOptions.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("global.no_results", { defaultValue: "No results found" })}
          />
        )}
      </div>
    </section>
  )

  return (
    <Select
      className={styles.picker}
      value={value}
      placeholder={placeholder}
      open={isOpen}
      onDropdownVisibleChange={setIsOpen}
      onChange={onChange}
      options={options}
      disabled={isDisabled}
      status={isInvalid ? "error" : undefined}
      showSearch={false}
      allowClear
      dropdownRender={renderDropdown}
      suffixIcon={<Icon name="ChevronDown" width={20} height={20} color="var(--color-text-primary)" />}
    />
  )
}

ReplacePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** `label` is what the closed field shows; `name`/`category` are the two halves the panel styles. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      name: PropTypes.string,
      category: PropTypes.string,
      searchText: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  /** The last save rejected this row's pick. */
  isInvalid: PropTypes.bool
}

export default ReplacePicker
